import { SGA_SPEND_BINDING_PLACEHOLDER_BY_LEVER } from './bindingPlaceholders';
import {
  SGA_SPEND_CONTEXT_KIND_BY_LEVER,
  SGA_SPEND_CONTEXT_KINDS,
} from './contextKinds';
import {
  SGA_SPEND_EVIDENCE_KEY_BY_LEVER,
  SGA_SPEND_EVIDENCE_KEYS,
} from './evidenceKeys';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import {
  SGA_SPEND_SCHEMA_VERSION_BY_LEVER,
  SGA_SPEND_SCHEMA_VERSIONS,
} from './schemaVersions';
import {
  DATA_LAKE_SOURCE_CLASS_MAPPINGS,
  DECISION_AGENT_SOURCE_CLASSES,
} from './sourceClasses';
import { SGA_SPEND_STATE_KEY_BY_LEVER, SGA_SPEND_STATE_KEYS } from './stateKeys';

function sgaSpendEntry<LeverId extends keyof typeof SGA_SPEND_EVIDENCE_KEY_BY_LEVER>(
  leverId: LeverId
) {
  return defineModuleEvidenceEntry(SGA_SPEND_EVIDENCE_KEY_BY_LEVER[leverId], {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: SGA_SPEND_CONTEXT_KIND_BY_LEVER[leverId],
    schemaVersion: SGA_SPEND_SCHEMA_VERSION_BY_LEVER[leverId],
    bindingPlaceholders: [SGA_SPEND_BINDING_PLACEHOLDER_BY_LEVER[leverId]],
    domain: 'finance',
    freshnessCadence: 'monthly',
    requiredFor: ['ask_helix', 'decision_pack'],
    missingBehavior: 'block',
    description:
      'Approved SGA lever evidence pack published by the governed data-lake source; backs the proof-policy evidence ref of the same key.',
  });
}

export const SGA_SPEND_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'sga-spend',
  {
    professional_fees: sgaSpendEntry('professional_fees'),
    outside_contractors: sgaSpendEntry('outside_contractors'),
    indirect_procurement: sgaSpendEntry('indirect_procurement'),
    travel_and_expense: sgaSpendEntry('travel_and_expense'),
    shared_services: sgaSpendEntry('shared_services'),
    tech_software: sgaSpendEntry('tech_software'),
    real_estate_footprint: sgaSpendEntry('real_estate_footprint'),
    marketing_spend: sgaSpendEntry('marketing_spend'),
    financeLineReconciliation: defineModuleEvidenceEntry(
      SGA_SPEND_EVIDENCE_KEYS.financeLineReconciliation,
      {
        sourceClass: DECISION_AGENT_SOURCE_CLASSES.publicFiling,
        contextKind: SGA_SPEND_CONTEXT_KINDS.financeLineReconciliation,
        schemaVersion: SGA_SPEND_SCHEMA_VERSIONS.financePnlGovernedSnapshot,
        bindingPlaceholders: [],
        domain: 'finance',
        freshnessCadence: 'monthly',
        requiredFor: ['ask_helix', 'decision_pack'],
        missingBehavior: 'block',
        description:
          'Approved finance SG&A baseline line reconciliation source; Q04 governed-floor assembly must resolve it by tenant, source-as-of, approval state, and proof ref before use.',
      },
    ),
    ownerMap: defineModuleEvidenceEntry(SGA_SPEND_EVIDENCE_KEYS.ownerMap, {
      sourceClass: DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
      contextKind: SGA_SPEND_CONTEXT_KINDS.ownerMap,
      schemaVersion: SGA_SPEND_SCHEMA_VERSIONS.ownerMap,
      bindingPlaceholders: [],
      domain: 'finance',
      freshnessCadence: 'monthly',
      requiredFor: ['ask_helix', 'decision_pack'],
      missingBehavior: 'block',
      description:
        'Approved SG&A owner-map and capacity-constraint source; Q04 governed-floor assembly must resolve it by tenant, strategy, scope, source-as-of, approval state, and proof ref before use.',
    }),
    controlPlan: defineModuleEvidenceEntry(SGA_SPEND_EVIDENCE_KEYS.controlPlan, {
      sourceClass: DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
      contextKind: SGA_SPEND_CONTEXT_KINDS.controlPlan,
      schemaVersion: SGA_SPEND_SCHEMA_VERSIONS.controlPlan,
      bindingPlaceholders: [],
      domain: 'finance',
      freshnessCadence: 'monthly',
      requiredFor: ['ask_helix', 'decision_pack'],
      missingBehavior: 'block',
      description:
        'Approved SG&A control-plan source; Q04 governed-floor assembly must resolve metric, tolerance, owner, cadence, escalation, decision binding, source-as-of, approval state, and proof ref before use.',
    }),
    governedFloor: defineModuleEvidenceEntry(SGA_SPEND_EVIDENCE_KEYS.governedFloor, {
      sourceClass: DECISION_AGENT_SOURCE_CLASSES.csvAdminUpload,
      contextKind: SGA_SPEND_CONTEXT_KINDS.governedFloor,
      schemaVersion: SGA_SPEND_SCHEMA_VERSIONS.governedFloor,
      bindingPlaceholders: [],
      domain: 'finance',
      freshnessCadence: 'monthly',
      requiredFor: ['ask_helix', 'decision_pack'],
      missingBehavior: 'block',
      description:
        'Approved SG&A Q04 objective floor, strategy and capacity constraints, and control plan. The bundle is admitted through the governed artifact and ingest-job boundary; missing or invalid state blocks the value bridge.',
    }),
  },
  {
    evidenceKeys: [
      ...Object.values(SGA_SPEND_EVIDENCE_KEY_BY_LEVER),
      ...Object.values(SGA_SPEND_EVIDENCE_KEYS),
    ],
    sourceClasses: [
      ...Object.values(DECISION_AGENT_SOURCE_CLASSES),
      ...Object.values(DATA_LAKE_SOURCE_CLASS_MAPPINGS).map(
        (mapping) => mapping.raw
      ),
    ],
    contextKinds: [
      ...Object.values(SGA_SPEND_CONTEXT_KIND_BY_LEVER),
      ...Object.values(SGA_SPEND_CONTEXT_KINDS),
    ],
    schemaVersions: [
      ...Object.values(SGA_SPEND_SCHEMA_VERSION_BY_LEVER),
      ...Object.values(SGA_SPEND_SCHEMA_VERSIONS),
    ],
    stateKeys: [
      ...Object.values(SGA_SPEND_STATE_KEY_BY_LEVER),
      ...Object.values(SGA_SPEND_STATE_KEYS),
    ],
    bindingPlaceholders: Object.values(SGA_SPEND_BINDING_PLACEHOLDER_BY_LEVER),
  },
);

export type SgaSpendModuleEvidenceContract = typeof SGA_SPEND_MODULE_EVIDENCE_CONTRACT;
